// frontend/src/components/chrome/Chrome.tsx
// Outer layout shell for nested routes. Renders one of two coordinated
// looks behind every page:
//   • The Writ, laid-paper + grain + a faint morphing guilloché.
//   • Stone, the chamber: the same ink, gold and oxblood on a cooler,
//     deeper paper, lit from high windows (the live Argument only).
// The look is chosen by route; light/dark is driven by `data-theme`.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigationType } from 'react-router-dom';
import Header from './Header';
import MapDialog from './MapDialog';
import Footer from './Footer';
import ToastRack from './ToastRack';
import { useStageStatus, useWorldScene } from '../../world/WorldStage';
import { roomForPath } from '../../world/rooms';
import { LeaveGuardProvider } from '../../lib/leaveGuard';
import { formatTitle } from '../../lib/documentTitle';

/** Human name for the current route, for the live region below. */
export function routeName(pathname: string): string {
  if (pathname === '/home') return 'The Docket';
  // NEW-PR-1: /record was missing, so opening Your Record announced "Case
  // not found". Chrome.test.tsx checks every shell route in App.tsx.
  if (pathname === '/record') return 'Your Record';
  if (pathname.startsWith('/case/')) return 'Case briefs';
  if (pathname.startsWith('/play/')) return 'Argument summary';
  if (pathname === '/achievements') return 'The Register';
  if (pathname === '/leaderboard') return 'The Bar';
  if (pathname === '/court') return 'The Court';
  if (pathname === '/guide') return 'Guide';
  if (pathname === '/settings') return 'Settings';
  if (pathname === '/about') return 'About';
  if (pathname === '/terms') return 'Terms and conditions';
  // Everything the shell routes is named above, so the fallback is the
  // catch-all route: the 404. It used to say "Page", which announced nothing
  // and was reachable only because the 404 was outside the shell.
  return 'Case not found';
}

/** Routes whose screens are not yet laid out in their room sit on a paper sheet until they are. */
const LEGACY_SCREENS: RegExp[] = [];

export default function Chrome() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const mainRef = useRef<HTMLElement>(null);
  const firstRender = useRef(true);
  const [announced, setAnnounced] = useState('');
  const { moving, mapOpen } = useStageStatus();
  // The route's room, at low priority: a screen that declares its own scene (with people) wins.
  useWorldScene({ room: roomForPath(location.pathname) }, 0);
  useLayoutEffect(() => {
    document.title = formatTitle(routeName(location.pathname));
  }, [location.pathname]);
  useEffect(() => {
    const coldLoad = firstRender.current && navigationType === 'POP';
    firstRender.current = false;
    if (coldLoad) return;
    setAnnounced(routeName(location.pathname));
    mainRef.current?.focus();
  }, [location.pathname, navigationType]);
  const inRoom = !LEGACY_SCREENS.some((r) => r.test(location.pathname));

  return (
    <LeaveGuardProvider>
      {/* Skip link: the HUD's controls come before any page content. */}
      <a
        href="#page-content"
        className="skip-link skip-link-px pb paper sm"
        onClick={(e) => {
          e.preventDefault();
          const el = document.getElementById('page-content');
          if (el) { el.focus(); el.scrollIntoView(); }
        }}
      >
        Skip to content
      </a>
      <Header hidden={mapOpen} />
      <main
        ref={mainRef}
        id="page-content"
        tabIndex={-1}
        className="room-ui full-screen-min-h"
        data-moving={moving || mapOpen}
        aria-label="Page content"
      >
        {inRoom ? <Outlet /> : <div className="legacy-sheet"><Outlet /></div>}
      </main>
      {/* Route announcer: focus moves to <main> on navigation; screen readers get the destination by name. */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announced}
      </div>
      <Footer />
      <ToastRack />
      <MapDialog />
    </LeaveGuardProvider>
  );
}
